import { detectFaces, type FaceDetectionResult } from './faceDetector';

/**
 * Live checks on the selfie preview, so the applicant is told what is wrong BEFORE they
 * capture — and cannot capture a frame we already know will fail.
 *
 * Two checks, applied in that order:
 *
 *   1. FACE PRESENCE / FRAMING, via {@link detectFaces} — is there exactly one face, big
 *      enough, roughly centred. This is the check that stops a photo of somebody's forehead
 *      being accepted as a selfie.
 *
 *   2. EXPOSURE — needs nothing but the pixels. A backlit or dim selfie is one of the most
 *      common reasons a real face match scores badly, and it is invisible to the applicant,
 *      who can see themselves perfectly well on a bright phone screen.
 *
 * The framing check can be unavailable (see faceDetector.ts — a failed asset load, an
 * unsupported device). It reports that as `undefined` rather than as "no face", and the
 * verdict falls back to exposure alone. An applicant is never trapped behind a red ring
 * because something failed to load, and `faceChecked` tells the caller which of the two
 * actually ran.
 */

/** What the preview is failing at, or null when it looks usable. */
export type FaceQualityReason =
  | 'no-face'
  | 'multiple-faces'
  | 'too-far'
  | 'too-close'
  | 'off-centre'
  | 'not-frontal'
  | 'too-dark';

export interface FaceQuality {
  /** Nothing wrong was detected — the ring goes green and capture is allowed. */
  ok: boolean;
  reason: FaceQualityReason | null;
  /** What to tell the applicant. Null when ok. */
  message: string | null;
  /**
   * Whether a real face detector produced this verdict. False means only exposure was
   * checked, which the caller may want to say differently — "looks bright enough" is not
   * "your face is in frame".
   */
  faceChecked: boolean;
}

/** A failing verdict for one reason, so callers do not each re-word the same message. */
export function faceQualityFailure(reason: FaceQualityReason, faceChecked = true): FaceQuality {
  return { ok: false, reason, message: MESSAGES[reason], faceChecked };
}

export const FACE_QUALITY_OK: FaceQuality = {
  ok: true,
  reason: null,
  message: null,
  faceChecked: false,
};

/**
 * Kept SHORT — three or four words. The hint pill is `whitespace-nowrap` by design (a
 * two-line pill reads as a warning rather than a nudge), so a long sentence does not wrap, it
 * pushes the pill wider than the card. These also change while the applicant moves, and a
 * short phrase is read at a glance where a sentence is not read at all.
 */
const MESSAGES: Record<FaceQualityReason, string> = {
  'no-face': 'Look at the camera',
  'multiple-faces': 'More than one face in frame',
  'too-far': 'Move a little closer',
  'too-close': 'Move back a little',
  'off-centre': 'Centre your face',
  'not-frontal': 'Look straight ahead',
  'too-dark': 'Too dark — find better light',
};

// ── Face presence and framing ─────────────────────────────────────────────────────────

/**
 * Framing thresholds, as a fraction of the shorter side of the frame.
 *
 * Tightened from 0.22-0.85 / 0.22 offset, which was too generous to mean anything: the video
 * is `object-cover` inside a circle, so only the CENTRE SQUARE of the frame is actually
 * visible, and a face 22% of the frame sitting 22% off centre could be half outside the circle
 * the applicant is being told to fill.
 *
 * 0.3-0.8 with a 0.15 offset is "clearly inside the circle, clearly big enough to match",
 * while still leaving room for somebody holding a phone naturally rather than at arm's length
 * with a spirit level.
 */
const MIN_FACE_FRACTION = 0.3;
const MAX_FACE_FRACTION = 0.8;
const MAX_CENTRE_OFFSET = 0.15;

/**
 * Face presence and framing for one frame.
 *
 * `undefined` means no detector could run at all, so the caller knows to fall back rather
 * than treating silence as "no face" and blocking a real applicant.
 */
export async function detectFaceFraming(
  video: HTMLVideoElement,
  timestampMs: number,
  knownFaces?: FaceDetectionResult,
): Promise<FaceQualityReason | null | undefined> {
  // `knownFaces` lets a caller that has ALREADY run a face model hand its result in rather
  // than paying for a second inference. The Liveness step does exactly that: its landmarker
  // produces both the blink score and the face box from one pass, so the ring is judged from
  // the same frame it is reacting to instead of loading BlazeFace alongside it.
  const faces = knownFaces !== undefined ? knownFaces : await detectFaces(video, timestampMs);
  if (faces === undefined) return undefined;

  if (faces.length === 0) return 'no-face';
  if (faces.length > 1) return 'multiple-faces';

  const face = faces[0];
  if (!face) return 'no-face';

  const shorterSide = Math.min(video.videoWidth, video.videoHeight);
  if (shorterSide === 0) return null;

  const fraction = Math.max(face.width, face.height) / shorterSide;
  if (fraction < MIN_FACE_FRACTION) return 'too-far';
  if (fraction > MAX_FACE_FRACTION) return 'too-close';

  const offsetX = Math.abs((face.x + face.width / 2) / video.videoWidth - 0.5);
  const offsetY = Math.abs((face.y + face.height / 2) / video.videoHeight - 0.5);
  if (offsetX > MAX_CENTRE_OFFSET || offsetY > MAX_CENTRE_OFFSET) return 'off-centre';

  return null;
}

// ── Exposure (works everywhere) ───────────────────────────────────────────────────────

/**
 * Mean luminance below which a frame is unusable, on 0-255.
 *
 * Deliberately LOW. The aim is to catch a frame that is genuinely too dark to verify, not
 * to insist on studio lighting — somebody verifying in a dim room at night must still be
 * able to finish, and a threshold that traps them is worse than a weaker match score.
 */
const MIN_MEAN_LUMA = 38;

/** The sampled square, in pixels. Small on purpose: this runs on every sampled frame. */
const SAMPLE_SIZE = 48;

/**
 * Mean luminance of the CENTRE of the frame, 0-255, or null when the frame is not readable.
 *
 * The centre, not the whole frame: a selfie taken against a window has a blown-out
 * background and a face in shadow, and averaging the whole frame reports that as
 * well-lit — the exact case this is meant to catch.
 */
export function measureCentreLuma(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): number | null {
  const { videoWidth: w, videoHeight: h } = video;
  if (w === 0 || h === 0) return null;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;

  // Centre square of the shorter side, scaled down to the sample size in one draw.
  const side = Math.min(w, h) * 0.6;
  try {
    ctx.drawImage(
      video,
      (w - side) / 2,
      (h - side) / 2,
      side,
      side,
      0,
      0,
      SAMPLE_SIZE,
      SAMPLE_SIZE,
    );
    const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

    let total = 0;
    // Rec. 601 luma, the standard perceptual weighting — a plain RGB average reads a blue
    // wall as brighter than it looks and a green one as darker.
    for (let i = 0; i < data.length; i += 4) {
      total += 0.299 * (data[i] ?? 0) + 0.587 * (data[i + 1] ?? 0) + 0.114 * (data[i + 2] ?? 0);
    }

    return total / (data.length / 4);
  } catch {
    // getImageData throws on a tainted canvas. A camera stream is same-origin, so this is
    // defensive rather than expected.
    return null;
  }
}

/**
 * The verdict for one frame: face framing where the browser supports it, exposure always.
 */
export async function assessFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  timestampMs: number,
  knownFaces?: FaceDetectionResult,
): Promise<FaceQuality> {
  const framing = await detectFaceFraming(video, timestampMs, knownFaces);
  const faceChecked = framing !== undefined;

  if (framing) {
    return { ok: false, reason: framing, message: MESSAGES[framing], faceChecked: true };
  }

  const luma = measureCentreLuma(video, canvas);
  if (luma !== null && luma < MIN_MEAN_LUMA) {
    return { ok: false, reason: 'too-dark', message: MESSAGES['too-dark'], faceChecked };
  }

  return { ok: true, reason: null, message: null, faceChecked };
}
